/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Modal, Form, Input, Alert } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';

export interface EditFolderProjectBuilderProps {
  visible: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description: string }) => void;
  folder: { id: number; name: string; description: string } | null;
  existingFolderNames?: string[];
}

export const EditFolderProjectBuilder: React.FC<EditFolderProjectBuilderProps> = ({
  visible,
  onClose,
  onSave,
  folder,
  existingFolderNames,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const nameInputRef = useRef<Input>(null);

  useEffect(() => {
    if (visible) {
      setTimeout(() => nameInputRef.current?.focus(), 350);
    }
  }, [visible]);

  const checkForDuplicate = (value: string) => {
    if (
      value &&
      existingFolderNames?.some(
        (name) => name.trim().toLowerCase() === value.trim().toLowerCase()
      )
    ) {
      setShowDuplicateWarning(true);
    } else {
      setShowDuplicateWarning(false);
    }
  };

  // Pre-populate form when folder changes
  useEffect(() => {
    if (visible && folder) {
      form.setFieldsValue({
        name: folder.name,
        description: folder.description || '',
      });
    }
  }, [visible, folder, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSave({
        name: values.name,
        description: values.description || '',
      });
    } catch (error) {
      // Validation failed
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setShowDuplicateWarning(false);
    onClose();
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={`${intl.formatMessage({ id: 'edit-folder-pb' })} "${
            folder?.name || ''
          }"`}
          icon={['fas', 'pen']}
        />
      }
      visible={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      centered>
      <Form form={form} layout="vertical" name="editFolderForm">
        <Form.Item
          name="name"
          label={intl.formatMessage({ id: 'folder-name' })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: 'folder-name-required' }),
            },
          ]}>
          <Input
            ref={nameInputRef}
            placeholder={intl.formatMessage({ id: 'enter-folder-name' })}
            onPressEnter={handleOk}
            onChange={(e) => checkForDuplicate(e.target.value)}
          />
        </Form.Item>
        {showDuplicateWarning && (
          <Alert
            message={intl.formatMessage({ id: 'folder-name-already-exists' })}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="description"
          label={intl.formatMessage({ id: 'description' })}>
          <Input.TextArea
            rows={3}
            placeholder={intl.formatMessage({
              id: 'enter-folder-description',
            })}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
